import React, { useEffect, useState } from 'react';
import { boilerplateApi } from '../../services/api';
import {
  PlusIcon,
  TrashIcon,
  PencilIcon,
  DocumentDuplicateIcon,
} from 'components/icons';
import Modal from '../../components/ui/Modal';
import PageHeader from '../../components/ui/PageHeader';

interface BoilerplateTemplate {
  id: string;
  type: string;
  title: string;
  content: string;
  tags: string[];
  naicsCodes: string[];
  version: number;
  isActive: boolean;
  notes?: string;
  createdAt: string;
  updatedAt: string;
}

const TYPE_OPTIONS: { value: string; label: string; section: string }[] = [
  { value: 'corporate_capabilities', label: 'Corporate Capabilities', section: 'Pulled into: Past Performance, Executive Summary, Technical Approach' },
  { value: 'qc_plan', label: 'Quality Control Plan', section: 'Pulled into: Management Plan' },
  { value: 'safety_plan', label: 'Safety Plan', section: 'Pulled into: Management Plan' },
  { value: 'transition_plan', label: 'Transition Plan', section: 'Pulled into: Management Plan' },
  { value: 'key_personnel_bio', label: 'Key Personnel Bio', section: 'Pulled into: Management Plan' },
  { value: 'teaming_approach', label: 'Teaming / Subcontractor Approach', section: 'Pulled into: Management Plan' },
  { value: 'security_plan', label: 'Security Plan', section: 'Optional / niche' },
  { value: 'technical_approach_template', label: 'Technical Approach Template', section: 'Pulled into: Technical Approach' },
  { value: 'management_approach_template', label: 'Management Approach Template', section: 'Pulled into: Management Plan' },
  { value: 'other', label: 'Other', section: 'Manual reference only' },
];

interface FormState {
  type: string;
  title: string;
  content: string;
  tags: string;
  naicsCodes: string;
  notes: string;
  isActive: boolean;
}

const EMPTY: FormState = {
  type: 'corporate_capabilities',
  title: '',
  content: '',
  tags: '',
  naicsCodes: '',
  notes: '',
  isActive: true,
};

const Boilerplate: React.FC = () => {
  const [items, setItems] = useState<BoilerplateTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [filterType, setFilterType] = useState<string>('');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState<FormState>(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const res = await boilerplateApi.list(filterType ? { type: filterType } : undefined);
      setItems(res.data?.data?.items || []);
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Failed to load');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterType]);

  const startCreate = () => {
    setEditingId(null);
    setForm(EMPTY);
    setShowForm(true);
  };

  const startEdit = (t: BoilerplateTemplate) => {
    setEditingId(t.id);
    setForm({
      type: t.type,
      title: t.title,
      content: t.content,
      tags: t.tags.join(', '),
      naicsCodes: t.naicsCodes.join(', '),
      notes: t.notes || '',
      isActive: t.isActive,
    });
    setShowForm(true);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);
    setError('');
    try {
      const payload = {
        type: form.type,
        title: form.title,
        content: form.content,
        tags: form.tags.split(',').map((s) => s.trim()).filter(Boolean),
        naicsCodes: form.naicsCodes.split(',').map((s) => s.trim()).filter(Boolean),
        notes: form.notes || null,
        isActive: form.isActive,
      };
      if (editingId) {
        await boilerplateApi.update(editingId, payload);
      } else {
        await boilerplateApi.create(payload);
      }
      setShowForm(false);
      setEditingId(null);
      setForm(EMPTY);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Delete this boilerplate template?')) return;
    try {
      await boilerplateApi.remove(id);
      await load();
    } catch (e: any) {
      setError(e?.response?.data?.error || e.message || 'Delete failed');
    }
  };

  const typeLabel = (v: string) => TYPE_OPTIONS.find((o) => o.value === v)?.label || v;

  return (
    <div className="p-6">
      <div className="flex items-center justify-between mb-6">
        <PageHeader
        title="Boilerplate Library"
        subtitle="Reusable approved company language: capability statements, QC plans, safety plans, transition plans, key personnel bios. The proposal writer pulls these as anchor content to adapt to each solicitation — saving you hours per bid."
      />
        <button
          onClick={startCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-gradient-to-r from-brand-600 to-purple-600 hover:from-brand-500 hover:to-purple-500"
        >
          <PlusIcon className="w-5 h-5" /> Add Template
        </button>
      </div>

      {error && (
        <div className="mb-4 p-3 rounded bg-red-50 border border-red-200 text-red-700 text-sm">
          {error}
        </div>
      )}

      {/* Type filter */}
      <div className="mb-4 flex items-center gap-2">
        <label className="text-sm text-content-muted">Filter:</label>
        <select
          value={filterType}
          onChange={(e) => setFilterType(e.target.value)}
          className="px-3 py-1.5 text-sm border border-border-strong rounded-md"
        >
          <option value="">All types</option>
          {TYPE_OPTIONS.map((o) => (
            <option key={o.value} value={o.value}>{o.label}</option>
          ))}
        </select>
      </div>

      {loading ? (
        <div className="text-content-muted text-sm">Loading…</div>
      ) : items.length === 0 ? (
        <div className="bg-surface rounded-lg border border-border p-12 text-center">
          <DocumentDuplicateIcon className="w-12 h-12 mx-auto text-content-subtle mb-4" />
          <h3 className="text-title text-content mb-2">No boilerplate templates yet</h3>
          <p className="text-sm text-content-muted mb-6 max-w-md mx-auto">
            Add your approved corporate capability statement, QC plan, safety plan, etc., once.
            The proposal writer will pull them automatically every time you generate a relevant section.
          </p>
          <button
            onClick={startCreate}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-md text-sm font-medium text-white bg-gradient-to-r from-brand-600 to-purple-600 hover:from-brand-500 hover:to-purple-500"
          >
            <PlusIcon className="w-5 h-5" /> Add Your First Template
          </button>
        </div>
      ) : (
        <div className="space-y-3">
          {items.map((t) => (
            <div
              key={t.id}
              className={`bg-surface rounded-lg border p-4 ${t.isActive ? 'border-border' : 'border-border opacity-60'}`}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-purple-100 text-purple-700 font-medium">
                      {typeLabel(t.type)}
                    </span>
                    {!t.isActive && (
                      <span className="text-xs text-content-subtle">inactive</span>
                    )}
                    <span className="text-xs text-content-subtle">v{t.version}</span>
                  </div>
                  <h3 className="font-medium text-content">{t.title}</h3>
                  <p className="text-sm text-content-muted mt-1 line-clamp-2">{t.content}</p>
                  <div className="flex flex-wrap gap-1 mt-2">
                    {t.tags.slice(0, 6).map((tag) => (
                      <span key={tag} className="inline-block text-xs px-2 py-0.5 rounded-full bg-surface-sunk text-content">
                        {tag}
                      </span>
                    ))}
                    {t.naicsCodes.length > 0 && (
                      <span className="inline-block text-xs px-2 py-0.5 rounded-full bg-blue-100 text-blue-700">
                        NAICS: {t.naicsCodes.join(', ')}
                      </span>
                    )}
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => startEdit(t)}
                    className="p-2 text-content-subtle hover:text-content-muted"
                    title="Edit"
                  >
                    <PencilIcon className="w-4 h-4" />
                  </button>
                  <button
                    onClick={() => remove(t.id)}
                    className="p-2 text-content-subtle hover:text-red-600"
                    title="Delete"
                  >
                    <TrashIcon className="w-4 h-4" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Modal form */}
      {/* Overlay dismissal is off — this form holds pasted boilerplate that
          would be lost to a stray backdrop click. Escape still closes. */}
      <Modal
        open={showForm}
        onClose={() => setShowForm(false)}
        dismissOnOverlayClick={false}
        title={`${editingId ? 'Edit' : 'Add'} Boilerplate Template`}
        size="xl"
        footer={
          <>
            <button type="button" onClick={() => setShowForm(false)} className="btn-secondary">
              Cancel
            </button>
            <button type="submit" form="boilerplate-form" disabled={saving} className="btn-primary">
              {saving ? 'Saving…' : editingId ? 'Save Changes' : 'Create Template'}
            </button>
          </>
        }
      >
            {/* The submit button lives in the modal footer, outside this
                element, so it targets the form by id. */}
            <form id="boilerplate-form" onSubmit={submit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-content mb-1">Type *</label>
                <select
                  required
                  value={form.type}
                  onChange={(e) => setForm({ ...form, type: e.target.value })}
                  className="input"
                >
                  {TYPE_OPTIONS.map((o) => (
                    <option key={o.value} value={o.value}>{o.label}</option>
                  ))}
                </select>
                <p className="text-xs text-content-muted mt-1">
                  {TYPE_OPTIONS.find((o) => o.value === form.type)?.section}
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-content mb-1">Title *</label>
                <input
                  type="text"
                  required
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  className="input"
                  placeholder="e.g., Standard Quality Control Plan — Mechanical Construction"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-content mb-1">
                  Content * — the actual reusable text
                </label>
                <textarea
                  required
                  rows={12}
                  value={form.content}
                  onChange={(e) => setForm({ ...form, content: e.target.value })}
                  className="input font-mono text-xs"
                  placeholder="Paste your approved company boilerplate here. The proposal writer will adapt this to each solicitation, not copy verbatim."
                />
                <p className="text-xs text-content-muted mt-1">
                  Word count: {form.content.split(/\s+/).filter(Boolean).length}
                </p>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-content mb-1">Tags</label>
                  <input
                    type="text"
                    value={form.tags}
                    onChange={(e) => setForm({ ...form, tags: e.target.value })}
                    className="input"
                    placeholder="HVAC, mechanical, three-phase inspection"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-content mb-1">
                    NAICS Codes (optional, restricts retrieval)
                  </label>
                  <input
                    type="text"
                    value={form.naicsCodes}
                    onChange={(e) => setForm({ ...form, naicsCodes: e.target.value })}
                    className="input"
                    placeholder="238220, 238210 — leave blank to apply broadly"
                  />
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-content mb-1">Internal Notes (optional)</label>
                <textarea
                  rows={2}
                  value={form.notes}
                  onChange={(e) => setForm({ ...form, notes: e.target.value })}
                  className="input"
                  placeholder="e.g., Last reviewed by [Name] in 2026. Approved for use on commercial federal contracts."
                />
              </div>

              <label className="flex items-center gap-2 text-sm text-content">
                <input
                  type="checkbox"
                  checked={form.isActive}
                  onChange={(e) => setForm({ ...form, isActive: e.target.checked })}
                />
                Active (uncheck to exclude from proposal generation without deleting)
              </label>

            </form>
      </Modal>
    </div>
  );
};

export default Boilerplate;
